import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function SchoolFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  const categories = [
    { id: "all", name: "Todas" },
    { id: "elementary", name: "Fundamental" },
    { id: "middle", name: "Médio" },
    { id: "high", name: "Superior" },
    { id: "technical", name: "Técnico" },
    { id: "language", name: "Idiomas" },
    { id: "other", name: "Outros" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/school?page=${pageNum}&limit=12`;
      if (category !== "all") url += `&category=${category}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching schools:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [category]);

  const handleEditPost = (post) => {
    setSelectedSchool(null);
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
    setSelectedSchool(null);
  };

  if (loading && posts.length === 0) {
    return <div className="school_feed_loading"><div className="school_loader"></div><span>Carregando escolas...</span></div>;
  }

  return (
    <div className="school_feed">
      <div className="school_header">
        <h1>Escolas & Instituições</h1>
        <p>Encontre a melhor instituição de ensino</p>
      </div>

      <div className="school_filters">
        {categories.map((cat) => (
          <button key={cat.id} className={category === cat.id ? "active" : ""} onClick={() => setCategory(cat.id)}>
            {cat.name}
          </button>
        ))}
      </div>

      {posts.length === 0 ? (
        <div className="school_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
          </svg>
          <h3>Nenhuma escola encontrada</h3>
          <p>Seja o primeiro a cadastrar uma instituição!</p>
        </div>
      ) : (
        <div className="school_grid">
          {posts.map((post) => (
            <div key={post._id} className="school_card" onClick={() => setSelectedSchool(post)}>
              <div className="school_image">
                <img src={post.schoolData?.image || post.images?.[0]?.url || post.images?.[0] || "/images/default-school.png"} alt="" />
                {post.schoolData?.rating && (
                  <span className="school_rating">⭐ {post.schoolData.rating.toFixed(1)}</span>
                )}
              </div>
              <div className="school_card_content">
                <span className="school_type">{categories.find(c => c.id === post.schoolData?.type)?.name || "Escola"}</span>
                <h3>{post.schoolData?.name || post.text}</h3>
                <p className="school_address">{post.schoolData?.address}</p>
                <div className="school_features">
                  {post.schoolData?.studentsCount && (
                    <span>{post.schoolData.studentsCount} alunos</span>
                  )}
                  {post.schoolData?.coursesCount && (
                    <span>{post.schoolData.coursesCount} cursos</span>
                  )}
                </div>
                <Link to={`/profile/${post.user.username}`} className="school_publisher" onClick={(e) => e.stopPropagation()}>
                  <img src={post.user.picture} alt="" />
                  <span>{post.user.first_name}</span>
                </Link>
                <div onClick={(e) => e.stopPropagation()}>
                  <FeedPostMenu
                    post={post}
                    onEdit={handleEditPost}
                    onPostDeleted={handlePostDeleted}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="school_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais escolas"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedSchool && (
        <div className="school_modal_overlay" onClick={() => setSelectedSchool(null)}>
          <div className="school_modal" onClick={(e) => e.stopPropagation()}>
            <button className="school_modal_close" onClick={() => setSelectedSchool(null)}>✕</button>
            <div className="school_modal_header">
              <img src={selectedSchool.schoolData?.image || selectedSchool.images?.[0]} alt="" />
            </div>
            <div className="school_modal_body">
              <span className="school_modal_type">{categories.find(c => c.id === selectedSchool.schoolData?.type)?.name}</span>
              <h2>{selectedSchool.schoolData?.name}</h2>
              {selectedSchool.schoolData?.rating && (
                <div className="school_modal_rating">⭐ {selectedSchool.schoolData.rating.toFixed(1)}</div>
              )}
              <p className="school_modal_desc">{selectedSchool.schoolData?.description || selectedSchool.text}</p>

              <div className="school_modal_info">
                {selectedSchool.schoolData?.address && (
                  <div className="school_info_item">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    </svg>
                    <span>{selectedSchool.schoolData.address}</span>
                  </div>
                )}
                {selectedSchool.schoolData?.phone && (
                  <div className="school_info_item">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                    </svg>
                    <span>{selectedSchool.schoolData.phone}</span>
                  </div>
                )}
                {selectedSchool.schoolData?.website && (
                  <div className="school_info_item">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-2.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.9-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.35.16-2h4.68c.09.65.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"/>
                    </svg>
                    <a href={selectedSchool.schoolData.website} target="_blank" rel="noopener noreferrer">
                      {selectedSchool.schoolData.website}
                    </a>
                  </div>
                )}
              </div>

              {selectedSchool.schoolData?.courses?.length > 0 && (
                <div className="school_courses">
                  <h4>Cursos Oferecidos</h4>
                  <div className="school_courses_list">
                    {selectedSchool.schoolData.courses.map((course, i) => (
                      <span key={i}>{course}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="school_modal_footer">
                <Link to={`/profile/${selectedSchool.user.username}`} className="school_modal_publisher">
                  <img src={selectedSchool.user.picture} alt="" />
                  <span>Publicado por {selectedSchool.user.first_name}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
